import { useState, useEffect } from 'react';

export type AppIconType = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'icon1' | 'icon2';

const STORAGE_KEY = 'secretchat_icon_preference';

export function getIconPath(icon: AppIconType): string {
  if (icon === 'icon1' || icon === 'icon2') {
    return `/images/${icon}.png`;
  }
  return `/secretchat-icon_${icon}.png`;
}

/**
 * Custom hook to manage the app icon preference.
 * - Reads/writes to localStorage.
 * - Dynamically updates the <link rel="icon">, <link rel="apple-touch-icon">, and <link rel="manifest">.
 */
export function useAppIcon() {
  const [icon, setIcon] = useState<AppIconType>(() => {
    if (typeof window === 'undefined') return 'A';
    const stored = localStorage.getItem(STORAGE_KEY);
    const validIcons = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'icon1', 'icon2'];
    if (stored && validIcons.includes(stored)) {
      return stored as AppIconType;
    }
    return 'A';
  });

  // Persist to storage and update DOM whenever icon changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, icon);

    const iconPath = getIconPath(icon);

    // 1. Update standard favicon
    let iconLink = document.querySelector('link[rel="icon"]');
    if (!iconLink) {
      iconLink = document.createElement('link');
      iconLink.setAttribute('rel', 'icon');
      iconLink.setAttribute('type', 'image/png');
      document.head.appendChild(iconLink);
    }
    iconLink.setAttribute('href', iconPath);

    // 2. Update iOS apple-touch-icon
    let appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (!appleTouchIcon) {
      appleTouchIcon = document.createElement('link');
      appleTouchIcon.setAttribute('rel', 'apple-touch-icon');
      document.head.appendChild(appleTouchIcon);
    }
    appleTouchIcon.setAttribute('href', iconPath);

    // 3. Update Android manifest dynamically using a Blob URL
    const manifest = {
      name: 'ChocoMesse',
      short_name: 'ChocoMesse',
      start_url: '/',
      display: 'standalone',
      background_color: '#0f172a',
      theme_color: '#1f2937',
      icons: [
        { src: iconPath, sizes: '192x192', type: 'image/png' },
        { src: iconPath, sizes: '512x512', type: 'image/png' }
      ]
    };

    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
    const manifestUrl = URL.createObjectURL(blob);

    let manifestLink = document.querySelector('link[rel="manifest"]');
    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.setAttribute('rel', 'manifest');
      document.head.appendChild(manifestLink);
    }
    // Clean up the old blob URL if we already had one created by this hook
    const oldHref = manifestLink.getAttribute('href');
    if (oldHref && oldHref.startsWith('blob:')) {
      URL.revokeObjectURL(oldHref);
    }
    manifestLink.setAttribute('href', manifestUrl);

    // Cleanup function when unmounting
    return () => {
      // We don't revoke the latest blob URL on unmount because the app needs it 
      // active for the browser to read it during "Add to Home Screen".
    };
  }, [icon]);

  return { icon, setIcon };
}
